import { useEffect, useMemo, useState } from 'react'

import type { ApolloError } from '@apollo/client'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import type { Channel, CommentStatus } from '~publish/gql/graphql'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { useCommentsConfig } from '../CommentsChannelConfigContext'
import { type CommentsFilters, useCommentsCounts } from './useCommentsCounts'
import type { FilterStatus } from '../CommentsFilters/types'

export type CommentCountsValues = {
  channelId: string | null
  count: number
  limit: number | null
}

type ApolloCountsState = {
  counts: CommentCountsValues[]
  loading: boolean
  error: ApolloError | null
}

type UseCommentCountResult = {
  counts: CommentCountsValues[] | null
  loading: boolean
  error: ApolloError | null
}

type ChannelCommentCountsProps = {
  channels: Channel[]
  status?: FilterStatus[]
  dateStart?: string
  dateEnd?: string
}

type PusherEventData = {
  channelId?: string
}

/* comment events that update the comments count */
const commentsEvents = [
  PusherEvent.COMMENT_DISMISSED,
  PusherEvent.COMMENT_REPLIED,
  PusherEvent.COMMENT_UNDISMISSED,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_POST,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_CHANNEL,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_ORGANIZATION,
  PusherEvent.COMMENT_INGESTED,
]

/**
 * Hook to fetch the number of new comments for an array of channels.
 * A new comment is an unreplied comment that has been created in the last 24 hours.
 *
 * @param channels - The channels to fetch comments count for
 * @param status - Optional array of status filters to apply
 * @param dateStart - Optional start date for filtering
 * @param dateEnd - Optional end date for filtering
 * @returns The number of comments for the channels, or null if loading/error/unsupported
 *
 * @example
 * const { counts, loading, error } = useNewCommentsCounts({
 *   channels: mockChannels,
 *   status: ['new'],
 *   publishedAt: {
 *     start: '2024-01-01',
 *     end: '2024-01-31'
 *   }
 * })
 **/
export function useChannelCommentCounts({
  channels,
  status,
  dateStart,
  dateEnd,
}: ChannelCommentCountsProps): UseCommentCountResult {
  const organizationId = useOrganizationId()
  const {
    config: { isSupportedInComments },
  } = useCommentsConfig()

  const supportedChannels = useMemo(
    () => channels.filter(isSupportedInComments),
    [channels, isSupportedInComments],
  )

  const supportedChannelIds = useMemo(() => {
    return supportedChannels.map((channel) => channel.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(supportedChannels.map((c) => c.id).sort())])

  const statusKey = useMemo(() => {
    return status ? JSON.stringify(status) : []
  }, [status])

  const memoizedStatus = useMemo(
    () => status || [],
    // We use statusKey as a dependency instead of status directly to prevent unnecessary
    // re-renders when the status array reference changes but its contents remain the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusKey],
  )

  const [state, setState] = useState<ApolloCountsState>({
    counts: [],
    loading: false,
    error: null,
  })

  const { fetchCounts } = useCommentsCounts()

  const fetchAndUpdateCounts = useCallbackRef(
    async ({ channelIds }: { channelIds: string[] }) => {
      if (!organizationId || !channelIds.length) {
        return
      }

      setState((prev) => ({ ...prev, loading: true }))

      const filters: CommentsFilters = {
        channelIds,
        ...(memoizedStatus &&
          memoizedStatus.length > 0 &&
          !memoizedStatus.some((status) => status === 'all') && {
            status: memoizedStatus as unknown as CommentStatus[],
          }),
        publishedAt: {
          start: dateStart ?? undefined,
          end: dateEnd ?? undefined,
        },
      }

      try {
        const { data, error: queryError } = await fetchCounts({
          organizationId,
          filters,
        })

        if (!data) {
          return
        }

        // update the counts for the changed channelIds
        setState((prev) => {
          const countsMap = new Map(
            prev.counts.map((count) => [count.channelId, count]),
          )

          data.commentsCounts.counts.forEach((count) => {
            if (count.channelId && channelIds.includes(count.channelId)) {
              countsMap.set(count.channelId, count)
            }
          })

          return {
            counts: Array.from(countsMap.values()),
            loading: false,
            error: queryError || null,
          }
        })
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          error: error as ApolloError,
        }))
      }
    },
  )

  useEffect(() => {
    void fetchAndUpdateCounts({ channelIds: supportedChannelIds })
  }, [
    fetchAndUpdateCounts,
    supportedChannelIds,
    memoizedStatus,
    dateStart,
    dateEnd,
  ])

  /**
   * Subscription to COMMENTS events that update the comments count
   */
  usePusherEvent([...commentsEvents], ({ channelId }: PusherEventData) => {
    if (channelId) {
      // channelId should be in the supportedChannelIds
      if (supportedChannelIds.includes(channelId)) {
        void fetchAndUpdateCounts({ channelIds: [channelId] })
      }
    } else {
      void fetchAndUpdateCounts({ channelIds: supportedChannelIds })
    }
  })

  return {
    counts: state.counts.length > 0 ? state.counts : null,
    loading: state.loading,
    error: state.error,
  }
}
